import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../utils/api';
import {
  Users, BarChart3, Cpu, DollarSign, CheckCircle, XCircle,
  ChevronDown, ChevronRight, Shield, LogOut, ArrowLeft,
  Pencil, Trash2, Plus, Loader, AlertCircle, Clock, FileSpreadsheet,
} from 'lucide-react';
import { TemplatesAdmin } from '../components/TemplatesAdmin';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = {
  date: (iso) => iso ? new Date(iso).toLocaleDateString() : '—',
  datetime: (iso) => iso
    ? `${new Date(iso).toLocaleDateString()} ${new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '—',
  num: (n) => (n ?? 0).toLocaleString(),
  cost: (n) => `$${(n ?? 0).toFixed(3)}`,
  dur: (s) => s != null ? `${s}s` : '—',
};

const StatusBadge = ({ status }) => {
  const map = {
    completed: 'bg-green-100 text-green-800',
    processing: 'bg-blue-100 text-blue-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || 'bg-neutral-100 text-neutral-700'}`}>
      {status}
    </span>
  );
};

// ─── Tab Navigation ──────────────────────────────────────────────────────────

const Tab = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 font-medium text-sm transition-colors border-b-2 ${
      active
        ? 'border-primary-600 text-primary-600'
        : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

// ─── Stats Cards ─────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color = 'primary' }) => {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-700',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-5 flex items-start gap-4">
      <div className={`p-3 rounded-lg ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm text-neutral-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-neutral-900">{value}</p>
        {sub && <p className="text-xs text-neutral-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

// ─── Session Row (expandable) ────────────────────────────────────────────────

const UserSessionsPanel = ({ userId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getUserSessions(userId)
      .then(r => setSessions(r.data))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <div className="flex items-center gap-2 py-4 pl-4 text-neutral-400">
      <Loader className="h-4 w-4 animate-spin" /> Loading sessions...
    </div>
  );

  if (!sessions.length) return (
    <p className="pl-4 py-4 text-sm text-neutral-400">No sessions yet</p>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-neutral-400 border-b border-neutral-100">
            <th className="text-left px-4 py-2 font-medium">File</th>
            <th className="text-left px-4 py-2 font-medium">Status</th>
            <th className="text-right px-4 py-2 font-medium">Rows</th>
            <th className="text-right px-4 py-2 font-medium">Tokens</th>
            <th className="text-right px-4 py-2 font-medium">Time</th>
            <th className="text-left px-4 py-2 font-medium">Date</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map(s => (
            <tr key={s.id} className="border-b border-neutral-50 hover:bg-neutral-50">
              <td className="px-4 py-2 max-w-[200px] truncate text-neutral-700">{s.original_filename}</td>
              <td className="px-4 py-2"><StatusBadge status={s.processing_status} /></td>
              <td className="px-4 py-2 text-right text-neutral-600">{s.rows_count ?? '—'}</td>
              <td className="px-4 py-2 text-right text-neutral-600">{fmt.num(s.total_tokens)}</td>
              <td className="px-4 py-2 text-right text-neutral-600">{fmt.dur(s.execution_seconds)}</td>
              <td className="px-4 py-2 text-neutral-500">{fmt.date(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────

const EditModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    is_admin: user.is_admin,
    is_trial: user.is_trial,
    trial_rows_limit: user.trial_rows_limit ?? '',
    trial_end_date: user.trial_end_date ? user.trial_end_date.slice(0, 10) : '',
    password: '',
    company_id: user.company_id ?? '',
    template_id: user.template_id ?? '',
  });
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load companies and templates
    Promise.all([
      adminApi.getCompanies(),
      adminApi.getTemplates(),
    ]).then(([companiesRes, templatesRes]) => {
      setCompanies(companiesRes.data);
      setTemplates(templatesRes.data);
    }).catch(console.error);
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        is_admin: form.is_admin,
        is_trial: form.is_trial,
        trial_rows_limit: form.trial_rows_limit !== '' ? parseInt(form.trial_rows_limit) : null,
        trial_end_date: form.trial_end_date ? new Date(form.trial_end_date).toISOString() : null,
        clear_trial_end_date: !form.trial_end_date,
        password: form.password || undefined,
        company_id: form.company_id || null,
        template_id: form.template_id || null,
      };
      await adminApi.updateUser(user.id, payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <h2 className="font-bold text-neutral-900">Edit {user.username}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl font-bold">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Toggles */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_admin}
                onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked }))}
                className="w-4 h-4 rounded accent-primary-600"
              />
              <span className="text-sm font-medium text-neutral-700">Admin</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_trial}
                onChange={e => setForm(f => ({ ...f, is_trial: e.target.checked }))}
                className="w-4 h-4 rounded accent-primary-600"
              />
              <span className="text-sm font-medium text-neutral-700">Trial</span>
            </label>
          </div>

          {/* Trial fields */}
          {form.is_trial && (
            <div className="space-y-3 pl-2 border-l-2 border-primary-100">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Rows limit</label>
                <input
                  type="number" min="1"
                  value={form.trial_rows_limit}
                  onChange={e => setForm(f => ({ ...f, trial_rows_limit: e.target.value }))}
                  placeholder="e.g. 500"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Trial end date</label>
                <input
                  type="date"
                  value={form.trial_end_date}
                  onChange={e => setForm(f => ({ ...f, trial_end_date: e.target.value }))}
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>
          )}

          {/* Password reset */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">New password (leave blank to keep)</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Enter new password"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          {/* Company */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Company</label>
            <select
              value={form.company_id}
              onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">No company</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Template */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Template override</label>
            <select
              value={form.template_id}
              onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">Auto (use company default)</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.template_name}</option>
              ))}
            </select>
            <p className="text-xs text-neutral-400 mt-1">Leave as Auto to use the company's default template</p>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

const DeleteModal = ({ user, onClose, onDeleted }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const doDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteUser(user.id);
      onDeleted();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-100 p-2 rounded-lg"><Trash2 className="h-5 w-5 text-red-600" /></div>
            <h2 className="font-bold text-neutral-900">Delete {user.username}</h2>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            This will permanently delete the account and all associated data. Type <strong>{user.username}</strong> to confirm.
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder={user.username}
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">Cancel</button>
          <button
            onClick={doDelete}
            disabled={confirmText !== user.username || deleting}
            className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex items-center gap-2"
          >
            {deleting && <Loader className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Create Modal ─────────────────────────────────────────────────────────────

const CreateModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    username: '', email: '', password: '',
    is_trial: false, trial_rows_limit: '', trial_duration_days: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await adminApi.createUser({
        username: form.username,
        email: form.email,
        password: form.password,
        is_trial: form.is_trial,
        trial_rows_limit: form.trial_rows_limit ? parseInt(form.trial_rows_limit) : null,
        trial_duration_days: form.trial_duration_days ? parseInt(form.trial_duration_days) : null,
      });
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-xs font-medium text-neutral-500 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <h2 className="font-bold text-neutral-900">Create User</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl font-bold">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {field('Username', 'username', 'text', 'john_doe')}
          {field('Email', 'email', 'email', 'john@example.com')}
          {field('Password', 'password', 'password', 'Min 8 characters')}

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_trial}
              onChange={e => setForm(f => ({ ...f, is_trial: e.target.checked }))}
              className="w-4 h-4 rounded accent-primary-600"
            />
            <span className="text-sm font-medium text-neutral-700">Free Trial</span>
          </label>

          {form.is_trial && (
            <div className="space-y-3 pl-2 border-l-2 border-primary-100">
              {field('Rows limit', 'trial_rows_limit', 'number', 'e.g. 500')}
              {field('Duration (days)', 'trial_duration_days', 'number', 'e.g. 30')}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">Cancel</button>
          <button
            onClick={save}
            disabled={!form.username || !form.email || !form.password || saving}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── User Row ─────────────────────────────────────────────────────────────────

const UserRow = ({ user, onEdit, onDelete, isCurrentUser }) => {
  const [expanded, setExpanded] = useState(false);

  const trialProgress = user.is_trial && user.trial_rows_limit
    ? Math.min(100, Math.round((user.trial_rows_used || 0) / user.trial_rows_limit * 100))
    : null;

  const isInactive = !user.last_active ||
    new Date(user.last_active) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  return (
    <>
      <tr
        className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Expand toggle */}
        <td className="pl-4 pr-2 py-3">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-neutral-400" />
            : <ChevronRight className="h-4 w-4 text-neutral-400" />}
        </td>

        {/* User */}
        <td className="py-3 pr-4">
          <div className="flex items-center gap-2">
            <span className="font-medium text-neutral-900 text-sm">{user.username}</span>
            {user.is_admin && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0.5 rounded font-semibold">Admin</span>
            )}
            {user.is_trial && (
              <span className="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5 rounded font-semibold">Trial</span>
            )}
            {isInactive && !user.is_admin && (
              <span className="bg-neutral-100 text-neutral-500 text-xs px-1.5 py-0.5 rounded">Inactive</span>
            )}
          </div>
          <p className="text-xs text-neutral-400 mt-0.5">{user.email}</p>
        </td>

        {/* Trial progress or sessions */}
        <td className="py-3 pr-4 text-sm text-neutral-600">
          {trialProgress !== null ? (
            <div>
              <div className="flex justify-between text-xs text-neutral-500 mb-1">
                <span>{fmt.num(user.trial_rows_used)} / {fmt.num(user.trial_rows_limit)} rows</span>
                <span>{trialProgress}%</span>
              </div>
              <div className="w-24 h-1.5 bg-neutral-200 rounded-full">
                <div
                  className={`h-1.5 rounded-full ${trialProgress >= 90 ? 'bg-red-500' : trialProgress >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                  style={{ width: `${trialProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <span>{fmt.num(user.total_sessions)} sessions</span>
          )}
        </td>

        {/* Stats */}
        <td className="py-3 pr-4 text-sm text-right text-neutral-700">{fmt.num(user.monthly_completed_files)}</td>
        <td className="py-3 pr-4 text-sm text-right text-neutral-700">{fmt.num(user.total_tokens)}</td>
        <td className="py-3 pr-4 text-sm text-right text-neutral-500 font-mono">{fmt.cost(user.estimated_cost_usd)}</td>

        {/* Last active */}
        <td className="py-3 pr-4 text-xs text-neutral-400">{fmt.date(user.last_active)}</td>

        {/* Joined */}
        <td className="py-3 pr-4 text-xs text-neutral-400">{fmt.date(user.created_at)}</td>

        {/* Actions */}
        <td className="py-3 pr-4" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(user)}
              className="p-1.5 rounded-lg hover:bg-primary-50 text-neutral-400 hover:text-primary-600 transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {!isCurrentUser && (
              <button
                onClick={() => onDelete(user)}
                className="p-1.5 rounded-lg hover:bg-red-50 text-neutral-400 hover:text-red-600 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-neutral-50 border-b border-neutral-100">
          <td colSpan={9} className="py-2">
            <UserSessionsPanel userId={user.id} />
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Main Admin Page ──────────────────────────────────────────────────────────

const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'users', 'templates'
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsers(),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      console.error('Failed to load admin data', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = () => {
    setLoading(true);
    fetchData();
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-neutral-50 to-primary-100 flex items-center justify-center">
      <Loader className="h-8 w-8 animate-spin text-primary-500" />
    </div>
  );

  const s = stats || {};
  const todayDelta = (s.sessions?.today ?? 0) - (s.sessions?.yesterday ?? 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-neutral-50 to-primary-100">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-800 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Dashboard
              </button>
              <div className="h-5 w-px bg-neutral-200" />
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-yellow-600" />
                <h1 className="text-lg font-bold text-neutral-900">Admin Panel</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-neutral-500">{user?.username}</span>
              <button
                onClick={logout}
                className="flex items-center text-neutral-500 hover:text-neutral-800 text-sm gap-1.5"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <Tab active={activeTab === 'overview'} icon={BarChart3} label="Overview" onClick={() => setActiveTab('overview')} />
            <Tab active={activeTab === 'users'} icon={Users} label="Users" onClick={() => setActiveTab('users')} />
            <Tab active={activeTab === 'templates'} icon={FileSpreadsheet} label="Templates" onClick={() => setActiveTab('templates')} />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard icon={Users} label="Total Users" value={s.users?.total ?? 0}
            sub={`${s.users?.active_30d ?? 0} active 30d`} color="primary" />
          <StatCard icon={CheckCircle} label="Completed" value={fmt.num(s.sessions?.completed)}
            sub={`of ${fmt.num(s.sessions?.total)} total`} color="green" />
          <StatCard icon={XCircle} label="Failed" value={fmt.num(s.sessions?.failed)}
            sub={`${s.sessions?.success_rate ?? 0}% success rate`} color="red" />
          <StatCard icon={BarChart3} label="Today" value={s.sessions?.today ?? 0}
            sub={todayDelta >= 0 ? `+${todayDelta} vs yesterday` : `${todayDelta} vs yesterday`}
            color="purple" />
          <StatCard icon={Cpu} label="Total Tokens" value={fmt.num(s.processing?.total_tokens)}
            sub={`avg ${s.processing?.avg_execution_seconds ?? 0}s/job`} color="yellow" />
          <StatCard icon={DollarSign} label="Est. Cost" value={`$${(s.processing?.estimated_cost_usd ?? 0).toFixed(2)}`}
            sub={`${fmt.num(s.processing?.total_rows)} rows processed`} color="green" />
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
            <h2 className="font-bold text-neutral-900">Users ({users.length})</h2>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" /> New User
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-100">
                <tr className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                  <th className="pl-4 pr-2 py-3 w-8" />
                  <th className="py-3 pr-4">User</th>
                  <th className="py-3 pr-4">Usage / Trial</th>
                  <th className="py-3 pr-4 text-right">Files (month)</th>
                  <th className="py-3 pr-4 text-right">Tokens</th>
                  <th className="py-3 pr-4 text-right">Cost</th>
                  <th className="py-3 pr-4">Last Active</th>
                  <th className="py-3 pr-4">Joined</th>
                  <th className="py-3 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isCurrentUser={u.id === user?.id}
                    onEdit={setEditUser}
                    onDelete={setDeleteUser}
                  />
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-neutral-400 text-sm">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && <TemplatesAdmin />}

      </main>

      {/* Modals */}
      {editUser && (
        <EditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); refresh(); }}
        />
      )}
      {deleteUser && (
        <DeleteModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onDeleted={() => { setDeleteUser(null); refresh(); }}
        />
      )}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </div>
  );
};

export default Admin;
